import { Theme } from "@mui/material";
import { NavigateFunction } from "react-router-dom";
import { DEFAULT_MAX_X_SCALE } from "@common/components/LineChart/const";
import { AnnotationOptions } from "chartjs-plugin-annotation";
import { MeetingInstance } from "@services/meetings/types";
import { getTimestampDiffInMinutes } from "@common/components/LineChart/utils";

const createMeetingBox = (start: number, end: number): AnnotationOptions => ({
  id: String(start),
  type: "box",
  xMin: start,
  xMax: end,
  backgroundColor: "#bfa1b7",
  borderColor: "transparent",
  drawTime: "beforeDatasetsDraw",
});

const createMeetingLabel = (
  meetingId: number,
  start: number,
  theme: Theme,
  navigateToMeetingPage: () => void
): AnnotationOptions => ({
  id: String(start),
  type: "label",
  content: String(meetingId),
  xValue: start,
  rotation: -90,
  borderRadius: 4,
  backgroundColor: theme.palette.common.white,
  font: { weight: 500 },
  drawTime: "afterDatasetsDraw",
  padding: 4,
  click: navigateToMeetingPage,
  enter: ({ chart }) => {
    chart.canvas.style.cursor = "pointer";
  },
  leave: ({ chart }) => {
    chart.canvas.style.cursor = "";
  },
  xAdjust: ({ chart }) => {
    const chartOptions = chart.options;
    const labelHeight = chartOptions.font?.size ?? 12;
    const xMin = Number(chartOptions.scales?.x?.min);
    const xMax = Number(chartOptions.scales?.x?.max || DEFAULT_MAX_X_SCALE);

    const leftOffset = xMin + labelHeight;
    const rightOffset = xMax - labelHeight;

    if (start < leftOffset) return labelHeight;

    if (start > rightOffset) return -labelHeight;

    return 0;
  },
});

const getMeetingTimeRange = (
  requestTimeRange: { start: number; end: number },
  meetingInstance: MeetingInstance
) => {
  const { start: reqStart, end: reqEnd } = requestTimeRange;
  const { start: meetingStart, end: meetingEnd } = meetingInstance;

  const startInMinutes =
    meetingStart! < reqStart
      ? 0
      : getTimestampDiffInMinutes(meetingStart!, reqStart);

  // if no meeting end time - take date.now, otherwise - take meeting end time if it less the request end time
  const meetingEndTime = meetingEnd ? Math.min(meetingEnd, reqEnd) : Date.now();
  const stopInMinutes = getTimestampDiffInMinutes(meetingEndTime, reqStart);

  return { startInMinutes, stopInMinutes };
};

export const getMeetingAnnotations = (
  meetingsList: MeetingInstance[],
  requestTimeRange: { start: number; end: number },
  theme: Theme,
  navigate: NavigateFunction,
  breadcrumbPage: string
) => {
  return meetingsList.reduce<Record<string, AnnotationOptions>>(
    (acc, meetingInstance, index) => {
      if (!meetingInstance.meetingId || !meetingInstance.start) return acc;

      const { startInMinutes, stopInMinutes } = getMeetingTimeRange(
        requestTimeRange,
        meetingInstance
      );

      acc[`meeting-box-${index}`] = createMeetingBox(
        startInMinutes,
        stopInMinutes
      );

      const navigateToMeetingPage = () =>
        navigate(`/meetings/${meetingInstance.id}`, {
          state: { previous: breadcrumbPage },
        });

      acc[`meeting-label-${index}`] = createMeetingLabel(
        meetingInstance.meetingId,
        startInMinutes,
        theme,
        navigateToMeetingPage
      );

      return acc;
    },
    {}
  );
};
